import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react"
import { useQuery } from "react-query"
import { useBoardConnection } from "./BoardConnection"
import useStickyState from "../hooks/useStickyState"
import { fetchGym, NotFoundError } from "../utils/gymApi"
import { clearWall, sendClimbsToWall } from "../utils/bluetooth"
import { slotEntriesFor, updatedKeyFor } from "../utils/gymDisplay"

export const GYM_POLL_INTERVAL_MS = 4000

const GymFollowContext = createContext({
  followed: null,
  follow: () => {},
  unfollow: () => {},
  sendGym: async () => {},
  blankBoard: async () => {},
})

/**
 * Keeps the connected board showing whatever the followed gym's page
 * shows, from any route. Opening a gym page follows it; from then on,
 * while this browser holds the board connection, every change anyone
 * makes to that gym's slots or colors is written to the wall as soon as
 * polling picks it up.
 *
 * This lets one Android phone act as the board's controller while
 * everyone else (iOS included, where the browser can't reach Bluetooth)
 * just edits the gym page.
 */
export function GymFollowProvider({ children }) {
  const { characteristic } = useBoardConnection()
  const [followed, setFollowed] = useStickyState(null, "followedGym")
  // Slot state last written to the board, and last queued for writing;
  // both keyed on slots and colors so an unchanged poll result is a no-op
  const lastSentRef = useRef(null)
  const queuedRef = useRef(null)
  // Writes are 20-byte chunks sent in series; two overlapping sends
  // would interleave and corrupt the frame, so they run one at a time
  const chainRef = useRef(Promise.resolve())
  const characteristicRef = useRef(characteristic)
  characteristicRef.current = characteristic

  const slug = followed?.slug ?? null

  const { data: gym } = useQuery(["gym", slug], () => fetchGym(slug), {
    enabled: Boolean(slug && characteristic),
    refetchInterval: (data, query) =>
      query.state.error instanceof NotFoundError ? false : GYM_POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
    retry: false,
  })

  const enqueue = useCallback((write) => {
    const run = chainRef.current.then(write).catch((err) => {
      console.error("Board write failed:", err)
    })
    chainRef.current = run
    return run
  }, [])

  const writeGym = useCallback(
    (state, key) =>
      enqueue(async () => {
        const target = characteristicRef.current
        if (!target) {
          return
        }
        const entries = slotEntriesFor(state)
        if (entries.length === 0) {
          await clearWall(target)
        } else {
          await sendClimbsToWall(target, entries)
        }
        lastSentRef.current = key
      }),
    [enqueue],
  )

  // A reconnect may follow a power cycle that lost what was on the board,
  // so forget what was sent and let the next render resend
  useEffect(() => {
    lastSentRef.current = null
    queuedRef.current = null
  }, [characteristic])

  const updatedKey = updatedKeyFor(gym)
  useEffect(() => {
    if (!characteristic || !gym || !slug || gym.slug !== slug) {
      return
    }
    if (
      lastSentRef.current === updatedKey ||
      queuedRef.current === updatedKey
    ) {
      return
    }
    queuedRef.current = updatedKey
    writeGym(gym, updatedKey)
  }, [characteristic, gym, slug, updatedKey, writeGym])

  const follow = useCallback(
    (next) =>
      setFollowed((prev) =>
        prev?.slug === next.slug && prev?.name === next.name ? prev : next,
      ),
    [setFollowed],
  )
  const unfollow = useCallback(() => setFollowed(null), [setFollowed])

  // Explicit "send now" from a page, e.g. after the user changed colors
  const sendGym = useCallback(
    (state) => {
      const key = updatedKeyFor(state)
      queuedRef.current = key
      return writeGym(state, key)
    },
    [writeGym],
  )

  const blankBoard = useCallback(
    () =>
      enqueue(async () => {
        const target = characteristicRef.current
        if (target) {
          await clearWall(target)
        }
      }),
    [enqueue],
  )

  return (
    <GymFollowContext.Provider
      value={{
        followed,
        follow,
        unfollow,
        sendGym,
        blankBoard,
      }}
    >
      {children}
    </GymFollowContext.Provider>
  )
}

export const useGymFollow = () => useContext(GymFollowContext)
